import React, { FormEvent, useState } from "react";
import { BadgePercent, Plus, Tag } from "lucide-react";
import {
  ActivityLog,
  EventCoupon,
  EventOperationsState,
  Sponsor,
} from "../../types";
import { createEventEntityId } from "../../lib/eventOperations";

interface EventCouponManagerProps {
  state: EventOperationsState;
  sponsors: Sponsor[];
  onChange: (state: EventOperationsState) => void;
}

const inputClass =
  "w-full h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-amber-300";

export const EventCouponManager: React.FC<EventCouponManagerProps> = ({
  state,
  sponsors,
  onChange,
}) => {
  const [editing, setEditing] = useState<EventCoupon | "new" | null>(null);
  const [error, setError] = useState("");

  const save = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const code = String(data.get("code") || "")
      .trim()
      .toUpperCase();
    const currentId = editing === "new" ? "" : editing?.id || "";
    if (
      state.coupons.some(
        (coupon) =>
          coupon.code.toUpperCase() === code && coupon.id !== currentId,
      )
    ) {
      setError("Ese código ya existe. Usa uno único.");
      return;
    }
    const coupon: EventCoupon = {
      id: currentId || createEventEntityId("coupon"),
      eventId: state.event.id,
      code,
      type: String(data.get("type") || "percentage") as EventCoupon["type"],
      value: Number(data.get("value") || 0),
      ticketTypeId: String(data.get("ticketTypeId") || "") || undefined,
      available: Number(data.get("available") || 0),
      used: editing === "new" ? 0 : editing?.used || 0,
      startsAt: String(data.get("startsAt") || ""),
      endsAt: String(data.get("endsAt") || ""),
      sponsorId: String(data.get("sponsorId") || "") || undefined,
      source: String(data.get("source") || "") || undefined,
      active: data.get("active") === "on",
    };
    const coupons = currentId
      ? state.coupons.map((item) => (item.id === currentId ? coupon : item))
      : [coupon, ...state.coupons];
    const activity: ActivityLog = {
      id: createEventEntityId("log"),
      eventId: state.event.id,
      entityType: "coupon",
      entityId: coupon.id,
      action: currentId ? "updated" : "created",
      description: `${currentId ? "Código actualizado" : "Código creado"}: ${coupon.code}.`,
      createdAt: new Date().toISOString(),
    };
    onChange({
      ...state,
      coupons,
      activityLogs: [activity, ...state.activityLogs],
      updatedAt: activity.createdAt,
    });
    setEditing(null);
    setError("");
  };

  return (
    <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display font-black text-slate-950 flex items-center gap-2">
            <Tag size={17} className="text-amber-600" /> Cupones & accesos
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Descuentos, entradas ocultas y códigos atribuibles a sponsors.
          </p>
        </div>
        <button
          onClick={() => {
            setEditing("new");
            setError("");
          }}
          className="h-9 px-3 rounded-xl bg-amber-400 text-slate-950 text-xs font-black flex items-center gap-1.5"
        >
          <Plus size={14} /> Nuevo
        </button>
      </div>
      <div className="p-4 grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {state.coupons.map((coupon) => {
          const sponsor = sponsors.find((item) => item.id === coupon.sponsorId);
          const ticket = state.ticketTypes.find(
            (item) => item.id === coupon.ticketTypeId,
          );
          return (
            <button
              key={coupon.id}
              onClick={() => {
                setEditing(coupon);
                setError("");
              }}
              className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-left hover:border-amber-300"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono font-black text-sm">
                  {coupon.code}
                </span>
                <span
                  className={`text-[9px] px-2 py-1 rounded-lg border font-black ${coupon.active ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-100 border-slate-200 text-slate-500"}`}
                >
                  {coupon.active ? "ACTIVO" : "PAUSADO"}
                </span>
              </div>
              <p className="text-xs font-bold text-slate-700 mt-3">
                {coupon.type.replaceAll("_", " ")} ·{" "}
                {coupon.type === "percentage"
                  ? `${coupon.value}%`
                  : coupon.value}
              </p>
              <p className="text-[10px] text-slate-500 mt-1">
                {coupon.used}/{coupon.available || "∞"} usos ·{" "}
                {ticket?.name || "Todos los tickets"}
              </p>
              {sponsor && (
                <p className="text-[10px] font-bold text-amber-700 mt-2">
                  Sponsor: {sponsor.companyName}
                </p>
              )}
            </button>
          );
        })}
        {state.coupons.length === 0 && (
          <div className="sm:col-span-2 xl:col-span-3 rounded-xl border border-dashed border-slate-300 p-8 text-center text-xs text-slate-500">
            Todavía no hay cupones configurados.
          </div>
        )}
      </div>
      {editing && (
        <form
          onSubmit={save}
          className="border-t border-slate-200 bg-amber-50/30 p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3"
        >
          <label className="text-[10px] font-black uppercase text-slate-500">
            Código
            <input
              name="code"
              required
              defaultValue={editing === "new" ? "" : editing.code}
              className={`${inputClass} mt-1`}
            />
          </label>
          <label className="text-[10px] font-black uppercase text-slate-500">
            Tipo
            <select
              name="type"
              defaultValue={editing === "new" ? "percentage" : editing.type}
              className={`${inputClass} mt-1`}
            >
              <option value="percentage">Porcentaje</option>
              <option value="fixed_amount">Monto fijo</option>
              <option value="free_ticket">Entrada gratis</option>
              <option value="hidden_ticket_access">
                Acceso a ticket oculto
              </option>
            </select>
          </label>
          <label className="text-[10px] font-black uppercase text-slate-500">
            Valor
            <input
              name="value"
              type="number"
              min="0"
              defaultValue={editing === "new" ? 10 : editing.value}
              className={`${inputClass} mt-1`}
            />
          </label>
          <label className="text-[10px] font-black uppercase text-slate-500">
            Disponibles
            <input
              name="available"
              type="number"
              min="0"
              defaultValue={editing === "new" ? 100 : editing.available}
              className={`${inputClass} mt-1`}
            />
          </label>
          <label className="text-[10px] font-black uppercase text-slate-500">
            Ticket
            <select
              name="ticketTypeId"
              defaultValue={editing === "new" ? "" : editing.ticketTypeId}
              className={`${inputClass} mt-1`}
            >
              <option value="">Todos</option>
              {state.ticketTypes.map((ticket) => (
                <option key={ticket.id} value={ticket.id}>
                  {ticket.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-[10px] font-black uppercase text-slate-500">
            Sponsor
            <select
              name="sponsorId"
              defaultValue={editing === "new" ? "" : editing.sponsorId}
              className={`${inputClass} mt-1`}
            >
              <option value="">Sin sponsor</option>
              {sponsors.map((sponsor) => (
                <option key={sponsor.id} value={sponsor.id}>
                  {sponsor.companyName}
                </option>
              ))}
            </select>
          </label>
          <label className="text-[10px] font-black uppercase text-slate-500">
            Inicio
            <input
              name="startsAt"
              type="date"
              defaultValue={
                editing === "new"
                  ? state.event.registrationOpenAt.slice(0, 10)
                  : editing.startsAt.slice(0, 10)
              }
              className={`${inputClass} mt-1`}
            />
          </label>
          <label className="text-[10px] font-black uppercase text-slate-500">
            Fin
            <input
              name="endsAt"
              type="date"
              defaultValue={
                editing === "new"
                  ? state.event.registrationCloseAt.slice(0, 10)
                  : editing.endsAt.slice(0, 10)
              }
              className={`${inputClass} mt-1`}
            />
          </label>
          <label className="text-[10px] font-black uppercase text-slate-500 sm:col-span-2">
            Fuente / partner
            <input
              name="source"
              defaultValue={editing === "new" ? "" : editing.source}
              className={`${inputClass} mt-1`}
            />
          </label>
          <label className="flex items-center gap-2 text-xs font-bold self-end h-10">
            <input
              name="active"
              type="checkbox"
              defaultChecked={editing === "new" ? true : editing.active}
            />{" "}
            Código activo
          </label>
          <div className="flex items-center justify-end gap-2 self-end">
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="h-10 px-3 rounded-xl border border-slate-200 bg-white text-xs font-black"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="h-10 px-4 rounded-xl bg-slate-950 text-white text-xs font-black flex items-center gap-2"
            >
              <BadgePercent size={14} /> Guardar
            </button>
          </div>
          {error && (
            <p className="sm:col-span-2 lg:col-span-4 text-xs font-bold text-rose-700">
              {error}
            </p>
          )}
        </form>
      )}
    </section>
  );
};
